import type { ReactNode } from 'react';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataGridToolbar } from './DataGridToolbar';
import { ColumnFilter } from './ColumnFilter';
import { PaginationBar } from './PaginationBar';
import { useInfiniteScroll } from './useInfiniteScroll';
import type { DataGridProps } from './types';

function StateRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-40 text-center text-sm text-muted-foreground">
        {children}
      </TableCell>
    </TableRow>
  );
}

function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: cols }).map((_unused, c) => (
            <TableCell key={c}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function ErrorState({ onRetry }: { onRetry?: (() => void) | undefined }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span>Something went wrong.</span>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

export function DataGrid<T>(props: DataGridProps<T>) {
  const {
    rows,
    columns,
    onRowClick,
    getRowId,
    isLoading,
    isError,
    isFetchingNextPage,
    endReached,
    onLoadMore,
    emptyMessage = 'No results',
    filterGroups,
    selectedFilters,
    onFiltersChange,
    onSortChange,
    sortableColumns,
    sortState,
    page,
    totalPage,
    onPageChange,
    isFetching,
  } = props;

  // Numbered paging when the caller wires it up; otherwise the legacy scroll sentinel.
  const usePager = !!onPageChange;

  const table = useReactTable<T>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => (getRowId ? getRowId(row) : String(index)), // stable keys -> no dup-key bug
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  // IntersectionObserver sentinel fires at ~80% — parity BaseListScreen.kt:89-103.
  const sentinelRef = useInfiniteScroll({
    onLoadMore,
    disabled: usePager || endReached || isLoading || isFetchingNextPage,
  });

  return (
    <div className="flex h-full flex-col rounded-xl border">
      <DataGridToolbar {...props} />

      {/* The ONE scroll container. `[&>div]:overflow-visible` neutralises the wrapper div that the
          shadcn <Table> emits (ui/table.tsx) — that wrapper is `overflow-auto`, which made it the
          nearest scrollport for the sticky <thead>, so the header scrolled away with the rows.
          Horizontal overflow simply moves up to this element, which is better anyway: the stuck
          header now scrolls sideways in sync with the body. */}
      <div className="relative flex-1 overflow-auto [&>div]:overflow-visible">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const columnId = header.column.id;
                  const groupDef = filterGroups?.find((g) => g.columnKey === columnId);
                  const sortable = !!onSortChange && !!sortableColumns?.includes(columnId);
                  const activeDir =
                    sortState && sortState.columnKey === columnId ? sortState.dir : null;
                  return (
                    <TableHead
                      key={header.id}
                      onClick={sortable ? () => onSortChange?.(columnId) : undefined}
                      // Background on the cell as well as the row so no row bleeds through the
                      // stuck header, and the bottom rule is an inset shadow because Tailwind's
                      // preflight sets border-collapse:collapse, under which Chrome drops the
                      // <tr> border once the thead is stuck.
                      className={cn(
                        'bg-muted shadow-[inset_0_-1px_0_var(--border)]',
                        sortable && 'cursor-pointer select-none',
                      )}
                    >
                      <div className="flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {sortable &&
                          (activeDir === 'asc' ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : activeDir === 'desc' ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/40" />
                          ))}
                        {groupDef && (
                          <ColumnFilter
                            group={groupDef}
                            selected={selectedFilters?.[groupDef.columnKey] ?? []}
                            onChange={(vals) =>
                              onFiltersChange?.({ ...selectedFilters, [groupDef.columnKey]: vals })
                            }
                          />
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isError ? (
              <StateRow colSpan={columns.length}>
                <ErrorState onRetry={props.onRetry} />
              </StateRow>
            ) : isLoading ? (
              <SkeletonRows rows={8} cols={columns.length} />
            ) : rows.length === 0 ? (
              <StateRow colSpan={columns.length}>{emptyMessage}</StateRow>
            ) : (
              table.getRowModel().rows.map((row, i) => (
                <TableRow
                  key={row.id}
                  data-even={i % 2 === 0}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className="cursor-pointer data-[even=true]:bg-muted/30 hover:bg-muted/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* load-more sentinel + footer spinner (parity BaseListScreen.kt:116-132) */}
        {!usePager && !endReached && !isError && rows.length > 0 && (
          <div ref={sentinelRef} className="flex h-16 items-center justify-center">
            {isFetchingNextPage && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
          </div>
        )}
      </div>

      {usePager && !isError && (
        <PaginationBar
          page={page ?? 1}
          totalPage={totalPage ?? 0}
          onPageChange={onPageChange}
          {...(isFetching !== undefined ? { isFetching } : {})}
        />
      )}
    </div>
  );
}
